import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import {
  CountdownParts,
  DEFAULT_UPDATE_COUNTDOWN_INTERVAL_MS,
  useCountdown,
} from '@/shared/hooks/use-countdown';
import { type AnyDate } from '@/shared/types/common';

import { ReactComponent as ClockIcon } from '@/assets/images/clock.svg?react';
import { ReactComponent as ColoredClockIcon } from '@/assets/images/clock-full-colored.svg?react';
import { ReactComponent as LockOpenIcon } from '@/assets/images/lock-open.svg?react';

import { TimerColors } from './types';

import s from './timer.module.scss';

export interface TimerProps {
  className?: string;
  classNameTime?: string;
  timestamp: (() => AnyDate) | AnyDate;
  text?: string;
  color?: TimerColors;
  simple?: boolean;
  onEnd?: () => void;
  interval?: number;
  noStyled?: boolean;
  noDays?: boolean;
  minPartsCount?: number;
  full?: boolean;
  isPlaycoinStickyBooster?: boolean;
  isMarketBooster?: boolean;
  withClock?: boolean;
  fullColoredClock?: boolean;
}

const MIN_TIME_PARTS = [CountdownParts.minutes, CountdownParts.seconds];

const COLORS = {
  [TimerColors.ORANGE]: s.orange,
  [TimerColors.GREEN]: s.green,
  [TimerColors.TRANSPARENT]: s.transparent,
  [TimerColors.NONE]: null,
};

const DURATION_KEY_BY_PART: Record<CountdownParts, string> = {
  [CountdownParts.years]: 'timer.years',
  [CountdownParts.months]: 'timer.months',
  [CountdownParts.days]: 'timer.days',
  [CountdownParts.hours]: 'timer.hours',
  [CountdownParts.minutes]: 'timer.minutes',
  [CountdownParts.seconds]: 'timer.seconds',
  [CountdownParts.milliseconds]: 'timer.milliseconds',
};

const DEFAULT_MIN_PARTS_COUNT = 2;

export const Timer = ({
  className,
  classNameTime,
  timestamp,
  text,
  color = TimerColors.NONE,
  simple = false,
  minPartsCount = DEFAULT_MIN_PARTS_COUNT,
  onEnd,
  interval = DEFAULT_UPDATE_COUNTDOWN_INTERVAL_MS,
  noStyled,
  withClock = true,
  fullColoredClock = false,
  isPlaycoinStickyBooster,
  isMarketBooster,
}: TimerProps) => {
  const { t } = useTranslation();
  const countdown = useCountdown(timestamp, { onEnd, interval });
  const { years, months, days, hours } = countdown;

  let time = '0:00';

  // isPlaycoinStickyBooster для специфического отображения часов в стики бустере
  const formatTime = () => {
    if (months > 0 && days === 0 && isPlaycoinStickyBooster) {
      return `${months}${t('timer.months').toUpperCase()}`;
    }

    if (hours > 0 && days === 0 && isPlaycoinStickyBooster) {
      return `${hours}${t('timer.hours')}`;
    }

    const timeParts = (Object.entries(countdown) as [CountdownParts, number][])
      .filter(([part, value]) => {
        if (part === CountdownParts.milliseconds && interval >= 1000) {
          return false;
        }

        if (MIN_TIME_PARTS.includes(part)) return true;
        return value > 0;
      })
      .slice(0, minPartsCount);

    if (days || months || years) {
      return timeParts
        .map(([key, value]) => `${value}${t(DURATION_KEY_BY_PART[key])}`)
        .join(' ');
    }

    return timeParts
      .map(([key, value]) => {
        let res = value;

        // Scale ms to 0-59
        if (key === CountdownParts.milliseconds) {
          res = Math.floor(((value % 1000) * 60) / 1000);
        }

        return res.toString().padStart(2, '0');
      })
      .join(':');
  };

  time = formatTime();

  if (simple) {
    return (
      <span className={noStyled ? undefined : clsx(s.simple, className)}>{time}</span>
    );
  }

  return (
    <div className={clsx(s.timer, !days && s['with-seconds'], COLORS[color], className)}>
      {text}
      <span className={clsx(s.time, classNameTime)}>
        {isMarketBooster ? (
          <LockOpenIcon className={s.icon} />
        ) : (
          withClock &&
          (fullColoredClock ? (
            <ColoredClockIcon className={s.icon} />
          ) : (
            <ClockIcon className={s.icon} />
          ))
        )}
        {time}
      </span>
    </div>
  );
};
